function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function safeUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw, window.location.origin);
    return ['http:', 'https:', 'data:'].includes(url.protocol) ? url.href : '';
  } catch (error) {
    return '';
  }
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('附件编码失败'));
    reader.readAsDataURL(blob);
  });
}

async function fetchAttachmentAsDataUrl(attachment) {
  const url = String(attachment?.url || '').trim();
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  let response;
  try {
    const attachmentUrl = new URL(url, window.location.origin);
    response = await fetch(attachmentUrl.href, {
      credentials: attachmentUrl.origin === window.location.origin ? 'same-origin' : 'omit'
    });
  } catch (error) {
    throw new Error(`附件“${attachment?.name || '未命名附件'}”无法读取，未生成导出文件`);
  }
  if (!response.ok) {
    throw new Error(`附件“${attachment?.name || '未命名附件'}”读取失败（HTTP ${response.status}），未生成导出文件`);
  }
  return blobToDataUrl(await response.blob());
}

async function embedConversationAttachments(messages) {
  const attachments = new Map();
  (messages || []).forEach(message => {
    const attachment = message?.attachment;
    const url = String(attachment?.url || '').trim();
    if (attachment && ['image', 'file'].includes(message?.type) && !url) {
      throw new Error(`附件“${attachment?.name || '未命名附件'}”缺少可读地址，未生成导出文件`);
    }
    if (url && !attachments.has(url)) attachments.set(url, attachment);
  });
  const entries = Array.from(attachments.entries());
  const embedded = new Map();
  const concurrency = 3;
  for (let start = 0; start < entries.length; start += concurrency) {
    const batch = entries.slice(start, start + concurrency);
    const results = await Promise.all(batch.map(async ([url, attachment]) => [url, await fetchAttachmentAsDataUrl(attachment)]));
    results.forEach(([url, dataUrl]) => embedded.set(url, dataUrl));
  }
  return {
    embeddedCount: embedded.size,
    messages: (messages || []).map(message => {
      const attachment = message?.attachment;
      if (!attachment?.url || !embedded.has(String(attachment.url))) return message;
      return {
        ...message,
        attachment: {
          ...attachment,
          url: embedded.get(String(attachment.url))
        }
      };
    })
  };
}

function fileSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value, timeZone) {
  const timestamp = Number(value || 0);
  if (!timestamp) return '时间未知';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone
  }).format(timestamp);
}

function messageType(message) {
  if (message?.recalled || message?.status === 'recalled') return '已撤回消息';
  if (message?.type === 'image') return '图片消息';
  if (message?.type === 'file') return '文件消息';
  if (message?.direction === 'system' || message?.type === 'system') return '系统消息';
  return '文字消息';
}

function renderAttachment(message) {
  const attachment = message?.attachment || {};
  const url = safeUrl(attachment.url);
  if (!url) return '';
  if (message.type === 'image') {
    return `<figure><a href="${escapeHtml(url)}" target="_blank" rel="noreferrer"><img src="${escapeHtml(url)}" alt="${escapeHtml(attachment.name || '图片')}"></a><figcaption>${escapeHtml(attachment.name || '图片')}</figcaption></figure>`;
  }
  const size = fileSize(attachment.size);
  return `<p class="attachment"><a href="${escapeHtml(url)}" download="${escapeHtml(attachment.name || '文件')}">下载文件：${escapeHtml(attachment.name || '文件')}</a>${size ? `<small>${escapeHtml(size)}</small>` : ''}</p>`;
}

function renderMessage(message, currentUserId, timeZone) {
  const own = message?.direction === 'out';
  const sender = own
    ? `我${currentUserId ? `（${currentUserId}）` : ''}`
    : message?.senderName || message?.from || '对方';
  const text = String(message?.text || '');
  const originalText = String(message?.originalText || '');
  const edited = Boolean(message?.editedAt);
  const recalled = Boolean(message?.recalled || message?.status === 'recalled');
  const content = [
    edited && originalText && originalText !== text
      ? `<div class="original"><strong>原文</strong><span>${escapeHtml(originalText)}</span></div>`
      : '',
    text ? `<p class="message-text">${escapeHtml(text)}</p>` : '',
    renderAttachment(message),
    recalled ? '<p class="state recalled">已撤回</p>' : '',
    edited ? `<p class="state">编辑于 ${escapeHtml(formatDate(message.editedAt, timeZone))}</p>` : ''
  ].filter(Boolean).join('');
  return `<article class="message ${own ? 'out' : message?.direction === 'system' ? 'system' : 'in'}">
    <div class="bubble">
      <header><strong>${escapeHtml(sender)}</strong><span>${escapeHtml(messageType(message))}</span></header>
      ${content || '<p class="message-text">[无可显示内容]</p>'}
      <footer>${escapeHtml(formatDate(message?.displayTimestamp || message?.timestamp, timeZone))}</footer>
    </div>
  </article>`;
}

export function buildConversationHtml({ conversation, messages, currentUserId = '', timeZone = '' }) {
  const resolvedTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const rows = [...(messages || [])]
    .sort((left, right) => Number(left?.displayTimestamp || left?.timestamp || 0) - Number(right?.displayTimestamp || right?.timestamp || 0));
  const title = conversation?.title || conversation?.target || '对话记录';
  const exportedAt = formatDate(Date.now(), resolvedTimeZone);
  const rangeStart = rows.length ? formatDate(rows[0]?.displayTimestamp || rows[0]?.timestamp, resolvedTimeZone) : '-';
  const rangeEnd = rows.length ? formatDate(rows.at(-1)?.displayTimestamp || rows.at(-1)?.timestamp, resolvedTimeZone) : '-';
  return `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)} - 对话记录</title>
  <style>
    :root{color-scheme:light;--bg:#eef3f5;--panel:#fff;--line:#d7e0e5;--text:#14232c;--muted:#667985;--in:#fff;--out:#dff5ef;--accent:#0f8f7c}*{box-sizing:border-box}body{margin:0;background:var(--bg);color:var(--text);font:14px/1.55 system-ui,-apple-system,"Segoe UI","PingFang SC",sans-serif}.page{width:min(980px,calc(100% - 28px));margin:28px auto}.summary{position:sticky;top:0;z-index:2;padding:20px;border:1px solid var(--line);border-radius:14px;background:rgba(255,255,255,.96);box-shadow:0 10px 32px rgba(25,45,55,.08)}h1{margin:0 0 10px;font-size:24px}.meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px 18px;color:var(--muted)}main{padding:24px 0}.message{display:flex;margin:0 0 14px}.message.out{justify-content:flex-end}.message.system{justify-content:center}.bubble{width:min(72%,720px);padding:11px 13px 9px;border:1px solid var(--line);border-radius:12px;background:var(--in);box-shadow:0 2px 8px rgba(25,45,55,.05)}.out .bubble{background:var(--out);border-color:#b8ded4}.system .bubble{width:auto;max-width:86%;background:#f7fafb;color:var(--muted)}.bubble header{display:flex;justify-content:space-between;gap:16px;margin-bottom:6px;color:var(--muted);font-size:12px}.bubble header strong{color:var(--text)}.message-text{margin:0;white-space:pre-wrap;overflow-wrap:anywhere}.bubble footer{margin-top:7px;color:var(--muted);font-size:11px;text-align:right}.original{display:grid;grid-template-columns:auto minmax(0,1fr);gap:8px;margin-bottom:8px;padding:7px 9px;border-left:3px solid var(--line);background:rgba(255,255,255,.55);color:var(--muted)}.original span{white-space:pre-wrap;overflow-wrap:anywhere}.state{margin:7px 0 0;color:var(--accent);font-size:12px}.state.recalled{color:#c43d2d;font-weight:700}figure{margin:8px 0 0}figure img{display:block;max-width:100%;max-height:520px;border-radius:9px}figcaption{margin-top:4px;color:var(--muted);font-size:11px}.attachment{display:flex;align-items:center;gap:10px;margin:8px 0 0}.attachment a{color:var(--accent);font-weight:700}.attachment small{color:var(--muted)}.note{padding:13px 16px;border:1px solid var(--line);border-radius:10px;background:var(--panel);color:var(--muted)}@media(max-width:640px){.page{width:calc(100% - 16px);margin:8px auto}.summary{padding:15px}.meta{grid-template-columns:1fr}.bubble{width:88%}}
  </style>
</head>
<body>
  <div class="page">
    <section class="summary">
      <h1>${escapeHtml(title)}</h1>
      <div class="meta"><span>会话ID：${escapeHtml(conversation?.target || conversation?.id || '-')}</span><span>记录数量：${rows.length}</span><span>起始：${escapeHtml(rangeStart)}</span><span>结束：${escapeHtml(rangeEnd)}</span><span>导出时间：${escapeHtml(exportedAt)}</span><span>时区：${escapeHtml(resolvedTimeZone)}</span></div>
    </section>
    <main>${rows.map(message => renderMessage(message, currentUserId, resolvedTimeZone)).join('\n')}</main>
    <p class="note">图片与文件均已内嵌在此 HTML 文件中，可在没有网络连接时完整阅读与下载。</p>
  </div>
</body>
</html>`;
}

function safeFilename(value) {
  return String(value || '对话记录').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').slice(0, 80) || '对话记录';
}

export async function downloadConversationHtml(options) {
  const offline = await embedConversationAttachments(options?.messages || []);
  const html = buildConversationHtml({ ...options, messages: offline.messages });
  const timestamp = new Date().toISOString().replaceAll(':', '-').replace(/\.\d{3}Z$/, 'Z');
  const filename = `${safeFilename(options?.conversation?.title || options?.conversation?.target)}-对话记录-${timestamp}.html`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return { filename, count: options?.messages?.length || 0, embeddedAttachments: offline.embeddedCount };
}
